import { Link } from "@tanstack/react-router";
import { ShieldCheck, Mail } from "lucide-react";
import { SafeImage } from "@/components/site/SafeImage";
import logoTransparent from "@/assets/lifewestway-logo-160.webp";
import { authorLabel } from "@/lib/article-variation";

type Props = {
  /** Article slug — varies the credit line wording per article. */
  slug?: string;
  name?: string;
  role?: string;
  bio?: string;
};

export function AuthorBio({
  slug,
  name = "LifeWestWay Editorial Team",
  role = "Research & fact-checking desk — not lawyers, accountants or licensed advisers",
  bio = "This guide is researched and written in-house. Every rule, fee, threshold and salary figure is checked against primary government sources (immigration departments, tax authorities and national statistics agencies) before publishing, each page names the sources it relies on, and we re-check fast-moving pages — fees, visa rules and tax bands — at least every quarter and after any announced policy change.",
}: Props) {
  return (
    <section
      aria-label="About the author"
      className="not-prose mt-12 overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-card via-card to-accent/[0.04] p-6 shadow-[0_1px_0_rgba(10,25,47,0.04)] sm:p-8"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="relative shrink-0">
          <div
            aria-hidden="true"
            className="grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-navy to-card p-2 ring-2 ring-accent/50"
          >
            <SafeImage
              src={logoTransparent}
              alt="LifeWestWay logo"
              width={64}
              height={64}
              noSkeleton
              className="h-full w-full object-contain"
            />
          </div>
          <span
            aria-hidden="true"
            className="absolute -bottom-1 -right-1 grid h-7 w-7 place-items-center rounded-full bg-accent text-foreground shadow"
          >
            <ShieldCheck className="h-4 w-4" />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">{slug ? authorLabel(slug) : "Written & fact-checked by"}</p>
          <h3 className="mt-1 font-display text-xl font-bold text-foreground">{name}</h3>
          <p className="text-sm font-medium text-muted-foreground">{role}</p>
          <p className="mt-3 text-sm leading-relaxed text-foreground/80">{bio}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Link title="About the LifeWestWay editorial team"
              to="/about"
              className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition hover:border-accent hover:text-accent"
            >
              About the team
            </Link>
            <Link title="Contact the LifeWestWay editorial team"
              to="/contact"
              aria-label="Contact the editorial team by email"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-navy/15 bg-card text-foreground transition hover:border-accent hover:text-accent"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link title="How LifeWestWay researches and fact-checks guides"
              to="/editorial-process"
              className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition hover:border-accent hover:text-accent"
            >
              How we research &amp; update
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AuthorBio;
